import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import locationService from '../services/locationService'

/**
 * Wiederverwendbare Regional Search Konfiguration
 * Verwendet in: DataExtraction (via RegionalSearchForm) und TemplateManager
 */
const RegionalSearchConfig = ({ 
  regionalConfig,
  onRegionalConfigChange,
  searchQuery = '',
  showSearchPreview = false,
  className = ''
}) => {
  const [states, setStates] = useState([])
  const [availableCities, setAvailableCities] = useState([])
  const [loadingStates, setLoadingStates] = useState(false)
  const [loadingCities, setLoadingCities] = useState(false)
  const [citySearchQuery, setCitySearchQuery] = useState('')

  // Load federal states on mount
  useEffect(() => {
    loadStates()
  }, [])

  // Load cities when state_code changes or when config is loaded from template
  useEffect(() => {
    if (regionalConfig?.state_code) {
      loadCities(regionalConfig.state_code)
    }
  }, [regionalConfig?.state_code])

  const loadStates = async () => {
    setLoadingStates(true)
    try {
      const data = await locationService.getFederalStates()
      if (data && Array.isArray(data) && data.length > 0) {
        setStates(data)
      } else {
        toast.error('Keine Bundesländer gefunden')
      }
    } catch (error) {
      console.error('Error loading states:', error)
      toast.error('Fehler beim Laden der Bundesländer')
    } finally {
      setLoadingStates(false)
    }
  }

  const loadCities = async (stateCode) => {
    if (!stateCode) {
      setAvailableCities([])
      return
    }

    setLoadingCities(true)
    try {
      const data = await locationService.getCitiesByState(stateCode, null) // null = alle Städte
      if (data && Array.isArray(data)) {
        setAvailableCities(data)
      } else {
        toast.error('Keine Städte gefunden')
      }
    } catch (error) {
      console.error('Error loading cities:', error)
      toast.error('Fehler beim Laden der Städte')
    } finally {
      setLoadingCities(false)
    }
  }

  const handleStateChange = async (stateCode) => {
    onRegionalConfigChange({
      ...regionalConfig,
      state_code: stateCode,
      cities: []
    })
  }

  const handleCityToggle = (city) => {
    const currentCities = regionalConfig.cities || []
    const isSelected = currentCities.some(c => c.zip === city.zip && c.city === city.city)
    
    const updatedCities = isSelected
      ? currentCities.filter(c => !(c.zip === city.zip && c.city === city.city))
      : [...currentCities, { zip: city.zip, city: city.city }]
    
    onRegionalConfigChange({
      ...regionalConfig,
      cities: updatedCities
    })
  }

  const handleSelectAll = () => {
    const filtered = getFilteredCities()
    onRegionalConfigChange({
      ...regionalConfig,
      cities: filtered.map(city => ({ zip: city.zip, city: city.city }))
    })
  }

  const handleDeselectAll = () => {
    onRegionalConfigChange({
      ...regionalConfig,
      cities: []
    })
  }

  const handleFormatChange = (field, value) => {
    onRegionalConfigChange({
      ...regionalConfig,
      [field]: value
    })
  }

  const getFilteredCities = () => {
    if (!citySearchQuery.trim()) return availableCities
    
    const query = citySearchQuery.toLowerCase().trim()
    return availableCities.filter(city =>
      city.city.toLowerCase().includes(query) ||
      city.zip.includes(query)
    )
  }

  const selectedState = states.find(s => s.code === regionalConfig?.state_code)
  const selectedCitiesCount = (regionalConfig?.cities || []).length
  const filteredCities = getFilteredCities()

  // Generate search preview
  const generateSearchPreview = () => {
    if (!searchQuery || !regionalConfig?.cities || regionalConfig.cities.length === 0) {
      return []
    }

    return regionalConfig.cities.slice(0, 5).map(city => {
      const parts = [searchQuery]
      if (regionalConfig.include_zip) parts.push(city.zip)
      if (regionalConfig.include_city) parts.push(city.city)
      return parts.join(' ')
    })
  }

  return (
    <div className={className}>
      {/* Bundesland auswählen */}
      <div className="mb-4">
        <label className="block text-sm font-bold text-dark-300 mb-2">
          Bundesland auswählen *
        </label>
        {loadingStates ? (
          <div className="text-sm text-dark-100">Lädt Bundesländer...</div>
        ) : (
          <select
            value={regionalConfig?.state_code || ''}
            onChange={(e) => handleStateChange(e.target.value)}
            className="input-field w-full"
            required
          >
            <option value="">Bitte wählen...</option>
            {states.map(state => (
              <option key={state.code} value={state.code}>
                {state.name} ({state.code}) - {state.city_count || 0} Orte
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Städte auswählen */}
      {regionalConfig?.state_code && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-bold text-dark-300">
              Städte auswählen ({selectedCitiesCount} ausgewählt)
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSelectAll}
                className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary-dark transition-colors"
              >
                Alle auswählen
              </button>
              <button
                type="button"
                onClick={handleDeselectAll}
                className="px-3 py-1 text-xs bg-dark-50 text-dark-300 rounded hover:bg-dark-100 transition-colors"
              >
                Alle abwählen
              </button>
            </div>
          </div>

          {/* Stadt-Suchfeld */}
          <div className="mb-2">
            <div className="relative">
              <i className="fa-solid fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-100"></i>
              <input
                type="text"
                value={citySearchQuery}
                onChange={(e) => setCitySearchQuery(e.target.value)}
                placeholder="Stadt suchen (Name oder PLZ)..."
                className="input-field w-full pl-10"
              />
            </div>
          </div>

          {/* Städte-Liste mit Checkboxen */}
          <div className="border-2 border-primary/20 rounded-lg p-3 max-h-64 overflow-y-auto bg-white">
            {loadingCities ? (
              <div className="text-sm text-dark-100 text-center py-4">
                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                Lädt Städte...
              </div>
            ) : filteredCities.length === 0 ? (
              <div className="text-sm text-dark-100 text-center py-4">
                {citySearchQuery ? 'Keine Städte gefunden' : 'Keine Städte verfügbar'}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {filteredCities.map((city, idx) => {
                  const isSelected = (regionalConfig.cities || []).some(
                    c => c.zip === city.zip && c.city === city.city
                  )
                  return (
                    <label
                      key={`${city.zip}-${idx}`}
                      className="flex items-center gap-2 cursor-pointer hover:bg-primary/5 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCityToggle(city)}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="text-sm text-dark-300">
                        {city.city}
                        <span className="text-dark-100 ml-1">({city.zip})</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Suchstring-Format */}
      <div className="mb-4">
        <label className="block text-sm font-bold text-dark-300 mb-2">
          <i className="fa-solid fa-cog mr-1"></i>
          Suchstring-Format
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={regionalConfig?.include_zip !== false}
              onChange={(e) => handleFormatChange('include_zip', e.target.checked)}
              className="w-4 h-4 text-primary"
            />
            <span className="text-sm text-dark-300">PLZ einbeziehen</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={regionalConfig?.include_city !== false}
              onChange={(e) => handleFormatChange('include_city', e.target.checked)}
              className="w-4 h-4 text-primary"
            />
            <span className="text-sm text-dark-300">Ort einbeziehen</span>
          </label>
        </div>
      </div>

      {/* Search Preview (optional) */}
      {showSearchPreview && searchQuery && selectedCitiesCount > 0 && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <h5 className="text-sm font-bold text-dark-300 mb-2">
            <i className="fa-solid fa-eye mr-1"></i>
            Vorschau der Suchanfragen
          </h5>
          <div className="text-xs text-dark-200 space-y-1">
            {generateSearchPreview().map((preview, idx) => (
              <div key={idx} className="font-mono bg-white px-2 py-1 rounded">
                → {preview}
              </div>
            ))}
            {selectedCitiesCount > 5 && (
              <div className="text-dark-100 italic">
                ... und {selectedCitiesCount - 5} weitere
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default RegionalSearchConfig
